import { useEffect, useRef, useState } from "react";
import styles from "./ChatContainer.module.scss";
import { FiSend } from "react-icons/fi";

import { auth, db } from "../../firebase";
import { collection, addDoc, serverTimestamp, onSnapshot, doc, deleteDoc } from "firebase/firestore";

export default function ChatContainer() {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const messagesContainerRef = useRef();

  const scrollToBottom = (smooth = true) => {
    if (messagesContainerRef.current) {
      if (smooth) {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: "smooth"
        });
      } else {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }
  };

  const getMessages = async () => {
    const snapshot = await db.collection("group-chat").get();
    const messages = snapshot.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id // Include document ID for deletion
    }));
    console.log(messages, "messages");
    // Sort by creation time - oldest first (newest at bottom)
    messages.sort((a, b) => a.createdAt.toDate() - b.createdAt.toDate());
    setMessages(messages);
  };

  useEffect(() => {
    getMessages();
  }, []);

  useEffect(() => {
    const unsubscribe = db.collection("group-chat").onSnapshot((snapshot) => {
      const newMessages = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id // Include document ID for deletion
      }));
      // Sort by creation time - oldest first (newest at bottom)
      newMessages.sort((a, b) => a.createdAt.toDate() - b.createdAt.toDate());
      setMessages(newMessages);
    });
  
    return () => unsubscribe(); // Cleanup on unmount
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (isInitialLoad) {
      // On initial load, scroll without animation
      scrollToBottom(false);
      setIsInitialLoad(false);
    } else {
      // For new messages, scroll with smooth animation
      scrollToBottom(true);
    }
  }, [messages]);

  const handleRightClick = (event, message) => {
    event.preventDefault();
    
    // Only allow deletion of your own messages
    if (message.uid === auth.currentUser.uid) {
      setMessageToDelete(message);
      setShowDeleteConfirm(true);
    }
  };

  const handleDeleteMessage = async () => {
    if (messageToDelete) {
      try {
        await deleteDoc(doc(db, "group-chat", messageToDelete.id));
        setShowDeleteConfirm(false);
        setMessageToDelete(null);
      } catch (error) {
        console.error("Error deleting message:", error);
        alert("Failed to delete message");
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setMessageToDelete(null);
  };

  const renderMessages = () => {
    if (messages.length === 0) {
      return (
        <div className={styles.noMessages}>
          No messages available. Be the first to send one!
        </div>
      );
    }
  
    return messages.map((item, index) => (
      <div
        className={`${
          item.uid === auth.currentUser.uid ? styles.currentUser : ""
        }`}
        key={index}
        onContextMenu={(e) => handleRightClick(e, item)}
        style={{ cursor: item.uid === auth.currentUser.uid ? 'context-menu' : 'default' }}
      >
        <div>
          <div className={styles.avatar}></div>
          <div>
            <p>{item.name}</p>
            <p>{item.text}</p>
            <p>
              {item.createdAt && item.createdAt.toDate()
                ? `${item.createdAt.toDate().toDateString()} ${item.createdAt.toDate().toLocaleTimeString("en-US")}`
                : "Date not available"}
            </p>
          </div>
        </div>
      </div>
    ));
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    if (message.trim() === "") {
      alert("Enter valid message");
      return;
    }
    const { uid, displayName, photoURL, email } = auth.currentUser;
    console.log(auth.currentUser, "sendMessage");
    await addDoc(collection(db, "group-chat"), {
      text: message,
      name: email,
      avatar: photoURL,
      createdAt: serverTimestamp(),
      uid,
    });
    setMessage("");
  };
  
  return (
    <div className={styles.container}>
      <p>Message Board</p>
      <div className={styles.msgs} ref={messagesContainerRef}>
        {renderMessages()}
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className={styles.deleteModal}>
          <div className={styles.deleteModalContent}>
            <h3>Delete Message</h3>
            <p>Are you sure you want to delete this message?</p>
            <div className={styles.deleteModalButtons}>
              <button 
                onClick={handleDeleteMessage}
                className={styles.deleteButton}
              >
                Delete
              </button>
              <button 
                onClick={handleCancelDelete}
                className={styles.cancelButton}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div>
        <input
          type="text"
          placeholder="Write Message Here"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => (e.key === "Enter" ? sendMessage(e) : null)}
        />
        <button onClick={(e) => sendMessage(e)}>
          <FiSend />
        </button>
      </div>
    </div>
  );
}
